import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Bot, Activity, X, Zap, Coffee, PowerOff, ListChecks } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Legend, PieChart, Pie, Cell,
} from "recharts";
import { api } from "../../lib/api";
import ModalPortal from "../../components/ModalPortal";
import { fmtDateTime } from "../../lib/ist";

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl border border-slate-200 shadow-sm ${className}`}>{children}</div>
);

const STATUS_META = {
  busy:    { label: "Busy",    dot: "bg-orange-500 animate-pulse", chip: "bg-orange-100 text-orange-700", icon: Zap },
  working: { label: "Working", dot: "bg-emerald-500", chip: "bg-emerald-100 text-emerald-700", icon: Activity },
  free:    { label: "Free",    dot: "bg-slate-300", chip: "bg-slate-100 text-slate-500", icon: Coffee },
  offline: { label: "Offline", dot: "bg-slate-400", chip: "bg-slate-200 text-slate-500", icon: PowerOff },
};
const TASK_COLORS = { completed: "text-emerald-600", failed: "text-rose-600", running: "text-orange-600" };
const PIE_APPROVAL = { pending: "#f59e0b", approved: "#10b981", rejected: "#f43f5e" };
const PIE_SEVERITY = { minor: "#94a3b8", moderate: "#f59e0b", major: "#f97316", critical: "#f43f5e", emergency: "#be123c" };

function AgentDetail({ agent, onClose }) {
  const [tasks, setTasks] = useState(null);
  useEffect(() => {
    api.get("/admin/ai-ops/tasks", { params: { agent: agent.key, limit: 40 } })
      .then(r=>setTasks(r.data.rows)).catch(()=>setTasks([]));
  }, [agent.key]);
  const meta = STATUS_META[agent.status];
  return (
    <ModalPortal>
      <div className="fixed inset-0 z-[100] bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
        <div className="w-full max-w-2xl bg-white rounded-2xl p-6 my-8" onClick={(e)=>e.stopPropagation()} data-testid="wf-agent-detail">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`}/>
                <h3 className="font-bold text-slate-900 text-lg">{agent.name}</h3>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${meta.chip}`}>{meta.label}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">{agent.role} · autonomy {agent.autonomy_level ?? "inherit"} · {agent.enabled ? "enabled" : "disabled"}</div>
              <p className="text-xs text-slate-500 mt-2 max-w-lg">{agent.description}</p>
            </div>
            <button onClick={onClose}><X size={18} className="text-slate-400"/></button>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4">
            {[["Tasks today", agent.tasks_today], ["Completed", agent.completed_today], ["Failed", agent.failed_today]].map(([t,v]) => (
              <div key={t} className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-center">
                <div className="text-xl font-extrabold text-slate-900">{v}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{t}</div>
              </div>
            ))}
          </div>
          <h4 className="font-bold text-slate-800 text-sm mt-5 mb-2 inline-flex items-center gap-1.5"><ListChecks size={14} className="text-emerald-600"/> Task history</h4>
          {!tasks ? <Loader2 className="animate-spin mx-auto my-6 text-emerald-600"/> :
            tasks.length === 0 ? <p className="text-xs text-slate-400">No tasks recorded yet for this agent.</p> : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
              {tasks.map(t => (
                <div key={t.id} className="flex items-center gap-2 text-xs bg-slate-50 rounded-lg px-3 py-2">
                  <span className={`font-bold uppercase ${TASK_COLORS[t.status] || "text-slate-500"}`}>{t.status}</span>
                  <span className="flex-1 text-slate-700 truncate">{t.title}</span>
                  <span className="text-slate-400 whitespace-nowrap">{t.duration_ms != null ? `${(t.duration_ms/1000).toFixed(1)}s · ` : ""}{fmtDateTime(t.started_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </ModalPortal>
  );
}

export default function AdminAIOpsWorkflow() {
  const [wf, setWf] = useState(null);
  const [stats, setStats] = useState(null);
  const [feed, setFeed] = useState(null);
  const [feedFilter, setFeedFilter] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/ai-ops/workflow").then(r=>setWf(r.data)).catch(()=>toast.error("Failed to load workflow"));
    api.get("/admin/ai-ops/tasks", { params: { limit: 40, ...(feedFilter ? { agent: feedFilter } : {}) } })
      .then(r=>setFeed(r.data.rows)).catch(()=>{});
  }, [feedFilter]);
  useEffect(() => {
    load();
    api.get("/admin/ai-ops/stats").then(r=>setStats(r.data)).catch(()=>{});
    const iv = setInterval(load, 6000);
    return () => clearInterval(iv);
  }, [load]);

  if (!wf) return <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-emerald-600"/></div>;

  return (
    <div className="space-y-5" data-testid="aiops-workflow">
      {/* status counters */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {[["busy", wf.counts.busy], ["working", wf.counts.working], ["free", wf.counts.free], ["offline", wf.counts.offline]].map(([k, v]) => {
          const m = STATUS_META[k];
          return (
            <Card key={k} className="p-3.5 flex items-center gap-2.5" >
              <span className={`w-3 h-3 rounded-full ${m.dot}`}/>
              <div>
                <div className="text-xl font-extrabold text-slate-900" data-testid={`wf-count-${k}`}>{v}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{m.label}</div>
              </div>
            </Card>
          );
        })}
        <Card className="p-3.5"><div className="text-xl font-extrabold text-emerald-600">{wf.tasks_today}</div><div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Tasks today</div></Card>
        <Card className="p-3.5"><div className="text-xl font-extrabold text-rose-600">{wf.failed_today}</div><div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Failed today</div></Card>
      </div>

      {/* live agent grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {wf.agents.map(a => {
          const m = STATUS_META[a.status];
          return (
            <Card key={a.key} className={`p-4 cursor-pointer hover:border-emerald-300 transition-colors ${a.status==="busy" && "ring-1 ring-orange-200"}`}>
              <div onClick={()=>setDetail(a)} data-testid={`wf-agent-${a.key}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${m.dot}`}/>
                  <Bot size={14} className={a.key==="ops_manager" ? "text-orange-500" : "text-emerald-600"}/>
                  <span className="font-bold text-slate-900 text-sm truncate">{a.name}</span>
                  <span className={`ml-auto text-[9px] font-bold uppercase px-2 py-0.5 rounded-full shrink-0 ${m.chip}`}>{m.label}</span>
                </div>
                <div className="mt-2 text-xs min-h-[32px]">
                  {a.current_task ? (
                    <div className="text-orange-700 bg-orange-50 border border-orange-100 rounded-lg px-2.5 py-1.5 truncate">
                      ⚡ {a.current_task.title}
                    </div>
                  ) : a.last_task ? (
                    <div className="text-slate-500 truncate">
                      Last: <span className={TASK_COLORS[a.last_task.status]}>{a.last_task.status}</span> · {a.last_task.title}
                    </div>
                  ) : <div className="text-slate-400">No tasks yet — idle</div>}
                </div>
                <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500">
                  <span>Today: <b className="text-slate-700">{a.tasks_today}</b></span>
                  <span className="text-emerald-600">✓ {a.completed_today}</span>
                  {a.failed_today > 0 && <span className="text-rose-600">✗ {a.failed_today}</span>}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* live task feed + charts */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-slate-900 text-sm inline-flex items-center gap-1.5"><Activity size={14} className="text-orange-500"/> Live task feed <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full uppercase">auto-refresh</span></h4>
            <select value={feedFilter} onChange={(e)=>setFeedFilter(e.target.value)} className="px-2 py-1 rounded-lg border border-slate-200 text-xs" data-testid="wf-feed-filter">
              <option value="">All agents</option>
              {wf.agents.map(a => <option key={a.key} value={a.key}>{a.name}</option>)}
            </select>
          </div>
          <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1" data-testid="wf-feed">
            {!feed || feed.length === 0 ? <p className="text-xs text-slate-400 py-6 text-center">No tasks yet — run a sweep, an AI command or a bug diagnosis to see live activity.</p> :
              feed.map(t => (
                <div key={t.id} className="flex items-center gap-3 text-xs bg-slate-50 rounded-lg px-3 py-2">
                  <span className={`font-bold uppercase w-20 shrink-0 ${TASK_COLORS[t.status] || "text-slate-500"}`}>{t.status}</span>
                  <span className="text-emerald-700 font-semibold w-28 shrink-0 truncate">{t.agent}</span>
                  <span className="flex-1 text-slate-700 truncate">{t.title}</span>
                  <span className="text-slate-400 whitespace-nowrap">{t.duration_ms != null ? `${(t.duration_ms/1000).toFixed(1)}s` : "…"}</span>
                </div>
              ))}
          </div>
        </Card>

        <Card className="p-4">
          <h4 className="font-bold text-slate-900 text-sm mb-2">Activity — last 14 days</h4>
          <div className="h-56" data-testid="wf-chart-activity">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats?.activity_14d || []}>
                <defs>
                  <linearGradient id="gA" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.5}/><stop offset="100%" stopColor="#10b981" stopOpacity={0}/></linearGradient>
                  <linearGradient id="gT" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f97316" stopOpacity={0.5}/><stop offset="100%" stopColor="#f97316" stopOpacity={0}/></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                <XAxis dataKey="date" tick={{ fontSize: 10 }}/>
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28}/>
                <Tooltip/>
                <Legend wrapperStyle={{ fontSize: 11 }}/>
                <Area type="monotone" dataKey="actions" stroke="#10b981" fill="url(#gA)" name="AI actions"/>
                <Area type="monotone" dataKey="tasks" stroke="#f97316" fill="url(#gT)" name="Agent tasks"/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-4 lg:col-span-1">
          <h4 className="font-bold text-slate-900 text-sm mb-2">Tasks per agent — 7 days</h4>
          <div className="h-56" data-testid="wf-chart-agents">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats?.agent_tasks_7d || []} layout="vertical" margin={{ left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }}/>
                <YAxis type="category" dataKey="name" width={92} tick={{ fontSize: 10 }}/>
                <Tooltip/>
                <Bar dataKey="completed" stackId="a" fill="#10b981" name="Completed" radius={[0,3,3,0]}/>
                <Bar dataKey="failed" stackId="a" fill="#f43f5e" name="Failed"/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-4">
          <h4 className="font-bold text-slate-900 text-sm mb-2">Approvals by status</h4>
          <div className="h-56" data-testid="wf-chart-approvals">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={stats?.approvals_by_status || []} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={3}>
                  {(stats?.approvals_by_status || []).map((e) => <Cell key={e.name} fill={PIE_APPROVAL[e.name] || "#94a3b8"}/>)}
                </Pie>
                <Tooltip/><Legend wrapperStyle={{ fontSize: 11 }}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-4">
          <h4 className="font-bold text-slate-900 text-sm mb-2">Alerts by severity</h4>
          <div className="h-56" data-testid="wf-chart-alerts">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={stats?.alerts_by_severity || []} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={3}>
                  {(stats?.alerts_by_severity || []).map((e) => <Cell key={e.name} fill={PIE_SEVERITY[e.name] || "#94a3b8"}/>)}
                </Pie>
                <Tooltip/><Legend wrapperStyle={{ fontSize: 11 }}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {stats?.avg_duration_7d?.length > 0 && (
        <Card className="p-4">
          <h4 className="font-bold text-slate-900 text-sm mb-2">Average task duration (7 days)</h4>
          <div className="flex flex-wrap gap-2">
            {stats.avg_duration_7d.map(d => (
              <div key={d.agent} className="px-3 py-2 rounded-lg bg-slate-50 border border-slate-100 text-xs">
                <b className="text-slate-800">{d.agent}</b> · {d.avg_sec}s avg · {d.count} tasks
              </div>
            ))}
          </div>
        </Card>
      )}

      {detail && <AgentDetail agent={detail} onClose={()=>setDetail(null)}/>}
    </div>
  );
}
